/**
 * The cache key for the server rendered home page.
 *
 * The rule, enforced by scripts/check-configcat-cache-key.mjs in CI:
 *
 * A flag the shiftnews home page renders from may only depend on cohort, the
 * device, whether the visitor is on the Shift browser, the engagement buckets
 * (daysSinceInstall and installDate) and the viewport class. Those are the
 * attributes the group cookie carries, and one cached HTML shell is shared by
 * every visitor whose cookie matches, so a rule on anything else serves the
 * first visitor's evaluation to all of them.
 *
 * A percentage rollout on such a flag must set its percentage attribute to
 * cohort. Left on the default, the rollout splits on the targeting key, which
 * is the whole cookie value, so the split lands per group rather than per user
 * and no longer honours the ratio it was set up with.
 *
 * Flags the home render never reads are exempt, and each tenant lists the ones
 * it does read in renderFlags below. Everything else is free to target whatever
 * it likes.
 */

/** The attributes a tenant's rendered home page may depend on. Nothing else reaches the server. */
export type GroupKeyAttribute =
  | 'cohort'
  | 'isMobile'
  | 'isShiftBrowser'
  | 'daysSinceInstall'
  | 'installDate'
  | 'viewport'
  | 'locale'

/** Attributes the group cookie carries as a bucket index rather than a raw value. */
export type BucketedAttribute = 'daysSinceInstall' | 'installDate' | 'viewport'

export interface TenantGroupKey {
  tenant: string // matches the ConfigCat environment
  configCatEnvironmentId: string
  enforce: boolean // true: CI fails on a violation. false: warns (tenant not on SSR yet)
  attributes: GroupKeyAttribute[] // in cookie order
  buckets: Partial<Record<BucketedAttribute, number[]>> // boundaries, lower bucket inclusive
  browserOnlyFlags: string[] // may split per user, the browser alone renders their outcome after mount
  renderFlags: string[] // the only flags the check looks at. Empty means the tenant has no list yet
}

export const CONFIGCAT_CONFIG_ID = '08de805b-59dc-4c4c-833d-eba8a0b74901'
export const QUERY_ATTRIBUTES = ['utmSource'] // from the URL, the CDN already keys on the query string
export const NON_RENDER_ATTRIBUTES = ['tabBadges'] // browser tab badge feature (useTabBadge.ts), never in the home HTML
export const GROUP_COOKIE = '_group'

/** Separates the attribute tokens inside the group cookie value. */
export const GROUP_COOKIE_SEPARATOR = '.'

/**
 * Token used when a tenant declares cohort but the request carries none. It is
 * reserved: a visitor whose cohort is literally `none` is written and read as
 * having no cohort, so both sides hand ConfigCat the same context.
 */
export const NO_COHORT = 'none'

/**
 * The shape a cohort token may take. Cohort is the only free form token in the
 * cookie, and the cookie comes from the client, so without a bound a visitor
 * could mint unlimited group values and every one of them is a cache variant
 * the origin has to render. It also keeps the token separator safe: a cohort
 * carrying a dot would split into extra tokens and the cookie would be
 * rejected.
 */
export const COHORT_PATTERN = /^[A-Za-z0-9_-]{1,32}$/

/** True for a token that names a cohort: the pattern, and not the reserved word. */
export function isCohortToken(value: string): boolean {
  return value !== NO_COHORT && COHORT_PATTERN.test(value)
}

/**
 * The language and country the page was rendered for, as one token, for example
 * en-US. Not a ConfigCat attribute and never handed to the SDK: it is here
 * because the news feed request carries language and country, so a page cached
 * for a group would otherwise serve the first visitor's feed to everyone in it.
 *
 * The browser resolves it exactly as LocaleStore does, and the server prefers it
 * over the Accept-Language header whenever the cookie carries it.
 */
export const GROUP_LOCALE_PATTERN = /^[a-z]{2}-[A-Z]{2}$/

/** What LocaleStore falls back to when it can read no language at all. */
export const DEFAULT_LOCALE = 'en-US'

/**
 * The locales a page is rendered for: every language LocaleStore supports,
 * paired with its default country, plus the English regions the traffic comes
 * from. The token is part of a cache key, so it has to be a closed list. A
 * locale off this list writes no group cookie and is refused by the server,
 * which keeps that visitor on the client shell they get today and stops made
 * up locales from each forcing a render.
 */
export const GROUP_LOCALES: readonly string[] = [
  'en-US',
  'en-GB',
  'en-CA',
  'en-AU',
  'es-MX',
  'fr-FR',
  'pt-BR',
  'de-DE',
  'it-IT',
  'pl-PL',
  'tr-TR',
  'ca-ES',
  'ko-KR',
  'uk-UA',
  'da-DK',
  'sv-SE',
  'no-NO',
  'hi-IN',
  'ja-JP',
  'tl-PH',
  'th-TH',
  'id-ID',
  'vi-VN',
  'ur-PK',
  'cs-CZ',
  'he-IL',
  'sw-KE',
  'bn-BD',
  'ro-RO',
  'hu-HU',
  'el-GR',
  'nl-NL',
  'ar-AE',
  'ta-SG',
]

/** True when the page is rendered for this locale. */
export function isGroupLocale(value: string): boolean {
  return GROUP_LOCALE_PATTERN.test(value) && GROUP_LOCALES.includes(value)
}

/**
 * Every viewport width at which the shiftnews home page renders something
 * different, so that one cached page per bucket is still the right page.
 *
 * 348, 660, 972, 1284 and 1596 are where the card grid gains a column
 * (utils/layout/calculateAutoLayoutColumnsForWidth.ts). 767 is where the test
 * section appears and 771 and 1595 are where the hero ad placement moves
 * between newtab_hero_mb, newtab_hero_tb and newtab_hero_dk, which reserve
 * different boxes (components/Layouts/Home.vue).
 *
 * Known limitation: the browser writes the bucket it measured on the previous
 * load, so a resize across a boundary followed by a reload is served the old
 * bucket once and the browser corrects it after hydration.
 */
export const VIEWPORT_BOUNDARIES = [348, 660, 767, 771, 972, 1284, 1595, 1596]

/**
 * Engagement thresholds a targeting rule may use without a code change. Wider
 * than what the rules need today on purpose, because moving a rule from 7 days
 * to 14 or 30 is a routine change and should not have to wait on a deploy.
 *
 * The lowest bucket is negative only, and -1 is its top so that 0, a visitor
 * who installed today, opens the first real bucket. A visitor outside the
 * Shift browser carries the default typetag 0_1000_100_1000_100_691231, whose
 * install date sentinel of 691231 makes daysSinceInstall a large negative
 * number. The browser evaluates the raw value, so `daysSinceInstall >= 0` is
 * false for those visitors, and the server has to hand ConfigCat a negative
 * value back or it would put them in the new user pool and render a different
 * page from the one they get after hydration.
 */
export const DAYS_SINCE_INSTALL_BOUNDARIES = [-1, 3, 7, 14, 30]

export const TENANT_GROUP_KEYS: TenantGroupKey[] = [
  {
    tenant: 'shiftnews',
    configCatEnvironmentId: '08de8058-9c86-4e70-8636-e1f3732dd12c',
    enforce: true,
    attributes: [
      'cohort',
      'isMobile',
      'isShiftBrowser',
      'daysSinceInstall',
      'installDate',
      'viewport',
      'locale',
    ],
    buckets: {
      daysSinceInstall: DAYS_SINCE_INSTALL_BOUNDARIES,
      installDate: [260710],
      viewport: VIEWPORT_BOUNDARIES,
    },
    browserOnlyFlags: ['nativeAdProvider'],
    // The flags the home render reads, and where each one is read. Everything
    // else in the config never reaches the cached HTML, so the check ignores it.
    renderFlags: [
      'splittestname', // useOpenFeature.browser.ts, feeds the config stores as custom.splittestname
      'layoutSkeleton', // pages/index.vue picks the home layout, and a boot payload store
      'siteSettings', // boot payload store, getConfigStorePaths.ts
      'layoutSettings', // boot payload store, getConfigStorePaths.ts
      'adSettings', // boot payload store, getConfigStorePaths.ts
      'summaryPageSettings', // boot payload store, inlined by bootPayloadConfigs.ts
      'discoverTab', // app.vue builds the tab list
      'enableLegoLand', // Layouts/Home.vue switches to the lego land render
      'engagedUserAdSlot', // Layouts/Home.vue swaps sponsored tiles for an ad slot
      'shiftNewsHome', // Layouts/Home.vue homepage layout test
      'shiftNewsSearchBoxVariant', // Layouts/Home.vue search box icon variant
      'searchEngineIconEnabled', // Layouts/Home.vue search engine icon kill switch
      'searchBoxVariant', // Layouts/Home.vue search box experiment
      'contentOrder', // ArticleCard.vue reorders the feed on click
      'absolutePublishedDateAfterHours', // ArticleCard.vue published date format
      'showCategoryInsteadOfSource', // useShowCategoryInsteadOfSource.ts, ArticleCard.vue
      'contentSource', // useNewsArticles.ts picks the feed
      'enableMixedArticleFeed', // useNewsArticles.ts article types
      'localNewsEnabled', // useNewsArticles.ts local publishers
      'randomizeFirstLoadOrder', // useNewsArticles.ts first load order
      'enableUsDefaultAllow', // 1.filters.client.ts starts consent from the server value
    ],
  },
  {
    tenant: 'onenews',
    configCatEnvironmentId: '08de8058-aa0d-48a8-8993-02f033ce4669',
    enforce: false,
    attributes: ['cohort', 'isMobile'],
    buckets: {},
    browserOnlyFlags: ['nativeAdProvider'],
    renderFlags: [],
  },
  {
    tenant: 'avanquest',
    configCatEnvironmentId: '08de8058-d2cb-4b8a-81f6-bebac31f1a1b',
    enforce: false,
    attributes: ['cohort', 'isMobile'],
    buckets: {},
    browserOnlyFlags: ['nativeAdProvider'],
    renderFlags: [],
  },
  {
    tenant: 'firearc',
    configCatEnvironmentId: '08de8059-617b-4479-8ffd-98a2a02b8d8f',
    enforce: false,
    attributes: ['cohort', 'isMobile'],
    buckets: {},
    browserOnlyFlags: [],
    renderFlags: [],
  },
  {
    tenant: 'couponroller',
    configCatEnvironmentId: '08de8059-6fcc-4cf8-89e5-06a2535d3c71',
    enforce: false,
    attributes: ['cohort', 'isMobile'],
    buckets: {},
    browserOnlyFlags: [],
    renderFlags: [],
  },
  {
    tenant: 'gendigital',
    configCatEnvironmentId: '08de8059-7dae-467a-8ca7-2c5c63ab6bc6',
    enforce: false,
    attributes: ['cohort', 'isMobile'],
    buckets: {},
    browserOnlyFlags: [],
    renderFlags: [],
  },
  {
    tenant: 'kite',
    configCatEnvironmentId: '08de8472-3672-4baa-8a3c-11bb29b6a9cf',
    enforce: false,
    attributes: ['cohort', 'isMobile'],
    buckets: {},
    browserOnlyFlags: [],
    renderFlags: [],
  },
  {
    tenant: 'discover-quartz',
    configCatEnvironmentId: '08dea0ad-4991-48bd-8b2c-7c4649cb79cc',
    enforce: true,
    // The shiftnews shape without installDate, which no Discover rule reads,
    // so it would only split the cache. The home is the same Layouts/Home.vue,
    // so the same widths change it.
    attributes: [
      'cohort',
      'isMobile',
      'isShiftBrowser',
      'daysSinceInstall',
      'viewport',
      'locale',
    ],
    buckets: {
      daysSinceInstall: DAYS_SINCE_INSTALL_BOUNDARIES,
      viewport: VIEWPORT_BOUNDARIES,
    },
    browserOnlyFlags: ['nativeAdProvider'],
    // The shiftnews list, which reads the same layout, and none of these may
    // target anything outside the group.
    renderFlags: [
      'splittestname',
      'layoutSkeleton',
      'siteSettings',
      'layoutSettings',
      'adSettings',
      'summaryPageSettings',
      'discoverTab',
      'enableLegoLand',
      'engagedUserAdSlot', // also pages/index.vue swaps sponsored tiles for an ad slot
      'shiftNewsHome',
      'shiftNewsSearchBoxVariant',
      'searchEngineIconEnabled',
      'searchBoxVariant',
      'contentOrder',
      'absolutePublishedDateAfterHours',
      'showCategoryInsteadOfSource',
      'contentSource',
      'enableMixedArticleFeed',
      'localNewsEnabled',
      'randomizeFirstLoadOrder',
      'enableSportsFeed',
      'enableUsDefaultAllow', // 1.filters.client.ts starts consent from the server value
    ],
  },
]

/**
 * Which bucket a boundary value itself belongs to. daysSinceInstall counts
 * elapsed days and its rules read "within the first N days", so N stays in the
 * bucket below. installDate is a yymmdd stamp and its rules read "installed on
 * or after D", so D opens the bucket above.
 */
export const BUCKET_EDGES: Record<BucketedAttribute, 'lower' | 'upper'> = {
  daysSinceInstall: 'lower',
  installDate: 'upper',
  // Every width dependent branch in the home render is written as
  // "width >= B", so B opens the bucket above it.
  viewport: 'upper',
}

/**
 * The bottom of every bucketed range. Not zero: daysSinceInstall goes negative
 * for a visitor whose install date is the typetag sentinel, and a check that
 * assumed a non negative domain would not notice a rule splitting the lowest
 * bucket at zero.
 */
const BUCKET_FLOOR = Number.MIN_SAFE_INTEGER

/** Stands in for "no upper limit" so ranges stay plain integers. */
const BUCKET_CEILING = Number.MAX_SAFE_INTEGER

/** An inclusive integer range of attribute values that share one bucket index. */
export interface BucketRange {
  index: number
  min: number
  max: number
}

export interface GroupAttributes {
  tenant: string
  cohort?: string
  isMobile?: boolean
  isShiftBrowser?: boolean
  daysSinceInstall?: number
  installDate?: number
  viewport?: number
  locale?: string
}

export function isBucketedAttribute(
  attribute: string
): attribute is BucketedAttribute {
  return (
    attribute === 'daysSinceInstall' ||
    attribute === 'installDate' ||
    attribute === 'viewport'
  )
}

export function getTenantGroupKey(tenant: string): TenantGroupKey | undefined {
  return TENANT_GROUP_KEYS.find((groupKey) => groupKey.tenant === tenant)
}

/** The ranges a tenant's boundaries carve the attribute into, lowest bucket first. */
export function bucketRanges(
  attribute: BucketedAttribute,
  boundaries: number[]
): BucketRange[] {
  const sorted = [...boundaries].sort((a, b) => a - b)
  const edge = BUCKET_EDGES[attribute]
  return sorted.concat(BUCKET_CEILING).map((_, index) => {
    const previous = sorted[index - 1]
    const next = sorted[index]
    if (edge === 'lower') {
      return {
        index,
        min: index === 0 ? BUCKET_FLOOR : previous + 1,
        max: next === undefined ? BUCKET_CEILING : next,
      }
    }
    return {
      index,
      min: index === 0 ? BUCKET_FLOOR : previous,
      max: next === undefined ? BUCKET_CEILING : next - 1,
    }
  })
}

export function bucketIndex(
  attribute: BucketedAttribute,
  boundaries: number[],
  value: number
): number {
  const range = bucketRanges(attribute, boundaries).find(
    (candidate) => value >= candidate.min && value <= candidate.max
  )
  return range ? range.index : 0
}

/**
 * A value the server can hand ConfigCat for a bucket. Every member of a bucket
 * evaluates the same way, which is exactly what the CI check enforces, so any
 * member does. The top of the range reads more like a real value than the floor.
 */
export function bucketRepresentative(range: BucketRange): number {
  return range.max === BUCKET_CEILING ? range.min : range.max
}

/**
 * Builds the group cookie value: one token per declared attribute, in order.
 * Returns null when the visitor's locale is not one the page is rendered for,
 * so the caller writes no cookie and the visitor keeps the client shell.
 */
export function groupCookieValue(attributes: GroupAttributes): string | null {
  const groupKey = getTenantGroupKey(attributes.tenant)
  if (!groupKey) {
    throw new Error(`No group key declared for tenant ${attributes.tenant}`)
  }

  const locale = attributes.locale ?? DEFAULT_LOCALE
  if (!isGroupLocale(locale)) {
    return null
  }

  return groupKey.attributes
    .map((attribute) => {
      if (attribute === 'cohort') {
        const cohort = attributes.cohort ?? ''
        return isCohortToken(cohort) ? cohort : NO_COHORT
      }
      if (attribute === 'isMobile') {
        return attributes.isMobile ? 'm' : 'd'
      }
      if (attribute === 'isShiftBrowser') {
        return attributes.isShiftBrowser ? 's' : 'w'
      }
      if (attribute === 'locale') {
        return locale
      }
      const boundaries = groupKey.buckets[attribute] || []
      const value = attributes[attribute] ?? BUCKET_FLOOR
      return String(bucketIndex(attribute, boundaries, value))
    })
    .join(GROUP_COOKIE_SEPARATOR)
}

/**
 * Turns a group cookie value back into the attributes the server hands
 * ConfigCat. Returns null when the cookie does not match what the tenant
 * declares, so the caller can fall back to the default group.
 */
export function parseGroupCookie(
  tenant: string,
  value: string
): GroupAttributes | null {
  const groupKey = getTenantGroupKey(tenant)
  if (!groupKey) {
    return null
  }

  const tokens = value.split(GROUP_COOKIE_SEPARATOR)
  if (tokens.length !== groupKey.attributes.length) {
    return null
  }

  const parsed: GroupAttributes = { tenant }
  for (const [position, attribute] of groupKey.attributes.entries()) {
    const token = tokens[position]
    if (!token) {
      return null
    }
    if (attribute === 'cohort') {
      if (token !== NO_COHORT) {
        if (!isCohortToken(token)) {
          return null
        }
        parsed.cohort = token
      }
      continue
    }
    if (attribute === 'isMobile') {
      if (token !== 'm' && token !== 'd') {
        return null
      }
      parsed.isMobile = token === 'm'
      continue
    }
    if (attribute === 'isShiftBrowser') {
      if (token !== 's' && token !== 'w') {
        return null
      }
      parsed.isShiftBrowser = token === 's'
      continue
    }
    if (attribute === 'locale') {
      if (!isGroupLocale(token)) {
        return null
      }
      parsed.locale = token
      continue
    }
    const range = bucketRanges(attribute, groupKey.buckets[attribute] || [])[
      Number(token)
    ]
    // Canonical digits only: "00" and "0" name the same bucket but would be two
    // cache entries and two renders, the unbounded variant problem the cohort
    // list already guards against.
    if (!/^\d+$/.test(token) || String(Number(token)) !== token || !range) {
      return null
    }
    parsed[attribute] = bucketRepresentative(range)
  }
  return parsed
}
